import { useState } from 'react';
import { DocumentStatus } from '@lombok/shared';
import { ApiError } from '@lombok/api-client';
import { Badge, Card, cn } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import { QueryState } from '@/components/QueryState';
import { api } from '@/lib/api';
import { useAccountNames, useDocumentReviewQueue, useReviewDocumentInQueue } from '@/data/hooks';
import {
  documentStatusLabel,
  documentStatusTone,
  documentTypeLabel,
} from './labels';

const TABS: { value: string; label: string }[] = [
  { value: DocumentStatus.PENDING, label: 'Pendientes' },
  { value: DocumentStatus.APPROVED, label: 'Aprobados' },
  { value: DocumentStatus.REJECTED, label: 'Rechazados' },
];

/**
 * Revisión de documentos (admin/finance). Lists uploaded onboarding documents by review
 * status across all accounts, oldest first, and lets a reviewer open the file (presigned)
 * and approve/reject. Approve/reject is audited server-side; the queue refreshes on action.
 */
export function RevisionDocumentosPage() {
  const [status, setStatus] = useState<string>(DocumentStatus.PENDING);
  const queue = useDocumentReviewQueue(status);
  const review = useReviewDocumentInQueue();
  const names = useAccountNames();
  const [acting, setActing] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const items = queue.data?.items ?? [];

  const open = async (accountId: string, documentId: string) => {
    setError(null);
    setOpening(documentId);
    try {
      const { url } = await api.getDocumentDownloadUrl(accountId, documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo abrir el documento.');
    } finally {
      setOpening(null);
    }
  };

  const decide = async (
    accountId: string,
    documentId: string,
    decision: 'approve' | 'reject',
  ) => {
    setError(null);
    setActing(documentId);
    try {
      await review.mutateAsync({ accountId, documentId, input: { decision } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo actualizar el documento.');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Revisión de documentos"
        description="Aprueba o rechaza los documentos de onboarding de las cuentas."
      />

      {/* Status tabs */}
      <div className="flex gap-1 rounded-lg border border-border bg-surface p-1">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setStatus(tab.value)}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              status === tab.value
                ? 'bg-accent-soft text-brand-primary'
                : 'text-text-muted hover:bg-surface-muted hover:text-text-primary',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error ? (
        <p className="rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <Card className="overflow-hidden">
        <QueryState
          isLoading={queue.isLoading}
          isError={queue.isError}
          error={queue.error}
          onRetry={() => void queue.refetch()}
          isEmpty={items.length === 0}
          loadingVariant="table"
          skeletonCols={4}
          emptyTitle={status === DocumentStatus.PENDING ? 'Nada por revisar' : 'Sin documentos'}
          emptyDescription={
            status === DocumentStatus.PENDING
              ? 'No hay documentos pendientes de revisión.'
              : 'No hay documentos en este estado.'
          }
        >
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-medium">Cuenta</th>
                <th className="px-4 py-3 font-medium">Documento</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 text-right font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => {
                const busy = acting === doc.documentId;
                return (
                  <tr key={doc.documentId} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      {(() => {
                        const account = names.data?.get(doc.accountId);
                        // Compare these with the file before approving it.
                        return account ? (
                          <>
                            <p className="font-medium text-text-primary">{account.name}</p>
                            <p className="text-xs text-text-muted">
                              RIF registrado: <span className="font-mono">{account.rif}</span>
                            </p>
                          </>
                        ) : (
                          <span className="font-mono text-xs text-text-muted">{doc.accountId}</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-primary">{documentTypeLabel(doc.type)}</p>
                      <p className="truncate text-xs text-text-muted">{doc.fileName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={documentStatusTone(doc.status)}>{documentStatusLabel(doc.status)}</Badge>
                      {doc.reviewedByName ? <p className="mt-1 text-xs text-text-muted">Revisado por {doc.reviewedByName}</p> : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowActionsMenu
                        actions={[
                          {
                            label: opening === doc.documentId ? 'Abriendo…' : 'Ver documento',
                            onSelect: () => void open(doc.accountId, doc.documentId),
                            disabled: opening === doc.documentId,
                          },
                          ...(doc.status === DocumentStatus.PENDING
                            ? [
                                {
                                  label: 'Aprobar',
                                  onSelect: () => void decide(doc.accountId, doc.documentId, 'approve'),
                                  disabled: busy,
                                },
                                {
                                  label: 'Rechazar',
                                  tone: 'danger' as const,
                                  onSelect: () => void decide(doc.accountId, doc.documentId, 'reject'),
                                  disabled: busy,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </QueryState>
      </Card>
    </div>
  );
}
